"use strict";
/**
 * Algolia geo search only reads a root `_geoloc` on each record (single point or array of points).
 * Provider feeds may store coordinates on `locations[]. _geoloc` (and/or legacy `locations[]. _geoLoc`)
 * without a root `_geoloc`. Merge so both shapes work for distance sort and "around" queries.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLocationGeoloc = getLocationGeoloc;
exports.mergeProviderRootGeoloc = mergeProviderRootGeoloc;
exports.coordinatesMatch = coordinatesMatch;
exports.locationMatchesRankingGeo = locationMatchesRankingGeo;
function toNumber(v) {
    if (typeof v === "number" && Number.isFinite(v))
        return v;
    if (typeof v === "string" && v.trim() !== "") {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
function normalizePoint(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const o = raw;
    const lat = toNumber(o.lat);
    const lng = toNumber(o.lng);
    if (lat === null || lng === null)
        return null;
    return { lat, lng };
}
/**
 * Reads coordinates from a single location object (Algolia or API shape).
 */
function getLocationGeoloc(loc) {
    if (!loc || typeof loc !== "object")
        return null;
    const o = loc;
    const nested = normalizePoint(o._geoloc) ??
        normalizePoint(o._geoLoc);
    return nested;
}
function geolocKey(p) {
    return `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
}
function dedupePoints(points) {
    const seen = new Set();
    const out = [];
    for (const p of points) {
        const k = geolocKey(p);
        if (seen.has(k))
            continue;
        seen.add(k);
        out.push(p);
    }
    return out;
}
function collectRootGeolocPoints(record) {
    const raw = record._geoloc;
    if (!raw)
        return [];
    if (Array.isArray(raw)) {
        return raw.map(normalizePoint).filter((p) => p !== null);
    }
    const one = normalizePoint(raw);
    return one ? [one] : [];
}
function collectLocationGeolocPoints(record) {
    const locs = record.locations;
    if (!Array.isArray(locs))
        return [];
    const out = [];
    for (const loc of locs) {
        const p = getLocationGeoloc(loc);
        if (p)
            out.push(p);
    }
    return out;
}
/**
 * Ensures `record._geoloc` is set for Algolia: keeps existing root point(s), adds any from
 * `locations[]. _geoloc` / `locations[]. _geoLoc`, dedupes. Does not mutate the input.
 */
function mergeProviderRootGeoloc(record) {
    const fromRoot = collectRootGeolocPoints(record);
    const fromLocs = collectLocationGeolocPoints(record);
    const merged = dedupePoints([...fromRoot, ...fromLocs]);
    if (merged.length === 0)
        return record;
    if (merged.length === 1) {
        return { ...record, _geoloc: merged[0] };
    }
    return { ...record, _geoloc: merged };
}
const COORD_EPS = 1e-4;
function coordinatesMatch(aLat, aLng, bLat, bLng) {
    return (Math.abs(aLat - bLat) < COORD_EPS && Math.abs(aLng - bLng) < COORD_EPS);
}
/**
 * True if this location's coordinates match Algolia's `matchedGeoLocation` for the hit.
 */
function locationMatchesRankingGeo(loc, matchedLat, matchedLng) {
    const p = getLocationGeoloc(loc);
    if (!p)
        return false;
    return coordinatesMatch(p.lat, p.lng, matchedLat, matchedLng);
}
